import { isEmpty } from 'lodash'
import { useCallback, useState } from 'react'

import styles from './FeedbackPostVotesInput.module.scss'

import { useDownvoteFeedbackPostMutation, useUpvoteFeedbackPostMutation } from '~generated/hooks'
import { FeedbackPostUserFragment } from '~generated/operations'
import { calculateAvatarColor } from '~lib/helpers'
import usePortalRoots from '~lib/hooks/usePortalRoots'
import Avatar from '~lib/ui-kit/Avatar'
import Popover from '~lib/ui-kit/Popover'
import Text from '~lib/ui-kit/Text'
import VotesInput from '~modules/feedbackTool/elements/VotesInput'

interface FeedbackPostVotesInputProps {
  feedbackPostId: string
  upvotes: number
  upvoters: FeedbackPostUserFragment[]
  upvotedByUser: boolean
  disabled?: boolean
  readOnly?: boolean
  className?: string
}

function FeedbackPostVotesInput({
  feedbackPostId,
  upvoters = [],
  upvotes,
  upvotedByUser,
  disabled,
  readOnly,
  className,
}: FeedbackPostVotesInputProps) {
  const [rootReference, setRootReference] = useState<HTMLDivElement>(null)

  const [upvoteFeedbackPostMutation, { loading: upvoteLoading }] = useUpvoteFeedbackPostMutation({
    notifyOnNetworkStatusChange: true,
  })

  const [downvoteFeedbackPostMutation, { loading: downvoteLoading }] = useDownvoteFeedbackPostMutation({
    notifyOnNetworkStatusChange: true,
  })

  const handleOnChange = useCallback(
    (value: number) => {
      if (value > upvotes) {
        upvoteFeedbackPostMutation({
          variables: { postId: feedbackPostId },
        })
      } else if (value < upvotes) {
        downvoteFeedbackPostMutation({
          variables: { postId: feedbackPostId },
        })
      }
    },
    [downvoteFeedbackPostMutation, feedbackPostId, upvoteFeedbackPostMutation, upvotes]
  )

  const { popupRoot } = usePortalRoots()

  return (
    <>
      <VotesInput
        ref={setRootReference}
        value={upvotes}
        cantDownvote={!upvotedByUser}
        readOnly={readOnly}
        disabled={disabled}
        loading={upvoteLoading || downvoteLoading}
        className={className}
        onChange={handleOnChange}
      />

      <Popover
        appendTo={popupRoot}
        anchorEl={rootReference}
        trigger="hover"
        placement="bottom-start"
        offset={[0, 8]}
        minWidth={210}
        maxWidth={210}
        disabled={isEmpty(upvoters)}
      >
        <div className={styles.menu}>
          <div className={styles.heading}>
            <Text variant="secondary" type="caption">
              Votes
            </Text>
          </div>

          <div className={styles.body}>
            <div className={styles.list}>
              {upvoters.map((upvoter) => {
                const label = upvoter.name || upvoter.email || 'Unnamed'

                return (
                  <div key={upvoter.id} className={styles.item}>
                    <Avatar
                      src={upvoter.avatarSrc}
                      alt={label}
                      color={calculateAvatarColor(label)}
                      size="sm"
                      className={styles.avatar}
                    />

                    <Text variant="default" type="body-s" className={styles.label}>
                      {label}
                    </Text>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

export default FeedbackPostVotesInput
